import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
// import * as constants from "Constants";
// import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
// import { oneLight as prismStyle } from "react-syntax-highlighter/dist/esm/styles/prism";
import { IoMdDownload } from "react-icons/io";
import { FaEdit } from "react-icons/fa";
import JSZip from "jszip";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
// import log from "loglevel";
import { IoCopyOutline } from "react-icons/io5";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import { getCommonEditorOptions } from "../CodeEditor/monacoEditorOptions";
const Editor = lazy(() => import('@monaco-editor/react'));

const editorOptions = getCommonEditorOptions();
editorOptions['readOnly'] = true;

function ShowCodeReadOnly({
  fileContent, // Should be an array of { name: string, content: string } for multiple files
  isEditEnabled,
  setIsEditEnabled,
  activityTitle,
  isStudentView = false, // Flag to indicate if this is for student view
}) {
  const codeRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [fileLanguages, setFileLanguages] = useState([]);
  // Update the language for each file based on its extension
  useEffect(() => {
    if (fileContent && fileContent.length > 0) {
      const languages = fileContent.map((file) => getFileExtension(file.name));
      setFileLanguages(languages);
    }
  }, [fileContent]);

  const copyFullCodeToClipboard = (e) => {

    if (fileContent?.[selectedTab]?.content) {
      const textToCopy = fileContent?.[selectedTab]?.content;
      navigator.clipboard.writeText(textToCopy);
      displayGlobalMessage("Copied to clipboard!", "success");
    } else {
      displayGlobalMessage(`There is no content in the ${fileContent?.[selectedTab]?.name} file to copy`, "error");
    }
  }


  // This function will return the extension of file
  const getFileExtension = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    // Map extensions to languages for SyntaxHighlighter
    switch (extension) {
      case "js":
        return "javascript";
      case "py":
        return "python";
      case "html":
        return "html";
      case "css":
        return "css";
      case "java":
        return "java";
      case "cpp":
      case "h":
        return "cpp";
      case "c":
        return "c";
      default:
        return extension;
    }
  };

  const downloadCode = () => {
    if (fileContent.length === 1) {
      // Single file download
      const file = fileContent[0];
      const blob = new Blob([file.content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // Multiple files: Create ZIP
      const zip = new JSZip();
      fileContent.forEach(({ name, content }) => {
        zip.file(name, content);
      });

      zip.generateAsync({ type: "blob" }).then((zipBlob) => {
        const zipUrl = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = zipUrl;
        a.download = `${activityTitle}.zip`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(zipUrl);
      });
    }
  };

  const handleTabs = (index) => {
    setSelectedTab(index);
  };

  return (
    <div className="cont">
      {/* Tabs for each file */}
      <ul className="nav nav-tabs">
        {fileContent.map((file, index) => (
          <li className="nav-item" key={index}>
            <a
              className={`nav-link ${selectedTab === index ? "active" : ""}`}
              onClick={() => handleTabs(index)}
              style={{ cursor: 'pointer' }}
            >
              {file.name || `File ${index + 1}`}
            </a>
          </li>
        ))}

        <div className="tab-buttons d-flex justify-content-between ml-auto">
          <Button
            variant="link"
            className="d-flex justify-content-center align-items-center"
            onClick={copyFullCodeToClipboard}
          >
            <OverlayTrigger overlay={<Tooltip>Copy code to clipboard</Tooltip>}>
              <IoCopyOutline />
            </OverlayTrigger>
          </Button>
          <Button
            variant="nav-link"
            maxHeight="20px"
            size="sm"
            onClick={() => {
              downloadCode()
            }}
            style={{ cursor: 'pointer', color: '#0056b3' }}
          >
            <OverlayTrigger overlay={<Tooltip>Download the code</Tooltip>}>
              <IoMdDownload />
            </OverlayTrigger>
          </Button>
          {
            !isStudentView &&
            <Button
              variant="link"
              className="d-flex justify-content-center align-items-center"
              onClick={() => setIsEditEnabled(true)}
            >
              <OverlayTrigger overlay={<Tooltip>Edit the code</Tooltip>}>
                <FaEdit />
              </OverlayTrigger>
            </Button>
          }
          
          {/* <Button variant="link" size="sm" className="d-flex justify-content-center align-items-center">
            <FaEllipsisV />
          </Button> */}
        </div>
      </ul>

      {/* Content for each file based on selected tab */}
      <div className="tab-content">
        {fileContent.map((file, index) => (
          <div
            key={index}
            className={`tab-pane fade ${selectedTab === index ? "show active" : ""} code-container`}
            style={{ maxHeight: "100vh" }} // Remove height: "100%" and overflow-auto from className
            // onCopy={(e) => handleCopy(e, file)}
            ref={codeRef}
          >
            <div style={{ height: "80vh", position: "relative" }}> {/* Fixed height container */}
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center w-100 h-100">
                  <Spinner size="sm" animation="border" />
                </div>
              }>
                <Editor
                  theme="light"
                  width="100%"
                  height="100%" // Height will be relative to parent
                  language={fileLanguages[index]}
                  value={file.content || "No valid submission found"}
                  options={editorOptions}
                  style={{ boxShadow: "0 1px 4px rgba(0, 0, 0, .1)" }}
                />
              </Suspense>
            </div>
          </div>
        ))}
      </div>
    </div >
  );
}

export default ShowCodeReadOnly;
